import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Mail, Trash2, CheckCircle, Circle, User, Briefcase } from 'lucide-react';
import {
  apiGetSubmissions,
  apiMarkSubmissionRead,
  apiDeleteSubmission,
  formatApiError,
} from '../../lib/api';
import { toast } from 'sonner';

export default function SubmissionsAdmin() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetSubmissions();
      setSubmissions(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await apiMarkSubmissionRead(id);
      toast.success('Marked as read');
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await apiDeleteSubmission(id);
      toast.success('Message deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const unreadCount = submissions.filter((s) => !s.read).length;

  return (
    <div className="space-y-6" data-testid="submissions-admin">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Contact Messages</h2>
        <p className="text-gray-500">
          {submissions.length} total message{submissions.length !== 1 && 's'}
          {unreadCount > 0 && (
            <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
              {unreadCount} unread
            </span>
          )}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-emerald-600" size={32} />
        </div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No messages yet. Form submissions will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {submissions.map((sub) => (
            <Card
              key={sub.id}
              className={`border-2 transition-all ${
                sub.read ? 'border-gray-100 bg-white' : 'border-emerald-300 bg-emerald-50/30'
              }`}
              data-testid={`submission-card-${sub.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      sub.type === 'brand' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {sub.type === 'brand' ? <Briefcase size={20} /> : <User size={20} />}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {sub.name}
                        {!sub.read ? (
                          <Circle size={10} className="fill-emerald-500 text-emerald-500" />
                        ) : (
                          <CheckCircle size={14} className="text-gray-400" />
                        )}
                      </CardTitle>
                      <p className="text-sm text-gray-600">
                        <a href={`mailto:${sub.email}`} className="text-blue-600 hover:underline">
                          {sub.email}
                        </a>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {sub.type === 'brand' ? '🏢 Brand inquiry' : '⭐ Creator inquiry'} · {formatDate(sub.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!sub.read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkRead(sub.id)}
                        data-testid={`mark-read-${sub.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Mark Read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(sub.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`delete-submission-${sub.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{sub.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
